import { ERROR_EXTRA_KEYS, type ApiError, type ErrorExtra, type Id, type Version } from '@contracts/common';

/**
 * The browser's only route to the API. It is deliberately small and deliberately dumb: it attaches the
 * session cookie, it unwraps the `{data}` envelope, and it turns any refusal into one typed error. What it
 * never does is retry. A mutation that failed is the person's to resend — the server decides whether a
 * replay is safe through the request id, and a client that guessed would turn one refused save into two.
 */

/** Everything a refusal carries. `fields` drives the error summary; `currentVersion` drives the conflict panel. */
export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string>,
    readonly currentVersion?: Version,
    /**
     * A refusal's payload: `OPEN_CHILDREN` names `openCount`, `TREE_DONE` the root (spec §5.4), a partner or
     * project refusal what still uses it, the duplicate it collides with, or where a gone partner went.
     */
    readonly extra?: ErrorExtra,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export type { ErrorExtra };
/** The earlier name, kept so existing imports compile. */
export type TreeErrorExtra = ErrorExtra;

export type ApiOptions = { method?: string; body?: unknown; requestId?: Id };

/** The copy shown when the server said nothing usable — a proxy error page, or no network at all. */
const GENERIC = 'Đã có lỗi xảy ra, vui lòng thử lại';
const OFFLINE = 'Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.';

/** No answer at all: the request never reached the server, or its answer never came back. */
export const offlineError = (): ApiClientError => new ApiClientError(0, 'NETWORK', OFFLINE);

export function envelopeError(status: number, payload: unknown): ApiClientError {
  const error = (payload as ApiError | undefined)?.error;
  if (!error || typeof error.code !== 'string') return new ApiClientError(status, 'UNKNOWN', GENERIC);
  // Every known payload key the server sent, `null` included (`mergedInto: null` means deleted, not merged).
  const extra: Record<string, unknown> = {};
  for (const key of ERROR_EXTRA_KEYS) if (error[key] !== undefined) extra[key] = error[key];
  return new ApiClientError(
    status, error.code, error.message || GENERIC, error.fields, error.currentVersion,
    Object.keys(extra).length > 0 ? extra : undefined,
  );
}

export async function api<T>(path: string, o: ApiOptions = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: o.method ?? 'GET',
      credentials: 'same-origin',
      headers: {
        // Only a request that sends JSON says so: Fastify answers 400 to a JSON content type with an empty body, which
        // is what a query-only DELETE (`?version=n`, the memory notes and the company knowledge) would otherwise send.
        ...(o.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(o.requestId ? { 'Idempotency-Key': o.requestId } : {}),
      },
      body: o.body === undefined ? undefined : JSON.stringify(o.body),
    });
  } catch {
    // A transport failure is indistinguishable from a lost response: the caller must keep its draft and
    // decide, so it is surfaced as an error of its own rather than as a generic server failure.
    throw offlineError();
  }
  if (response.status === 204) return undefined as T;
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }
  if (!response.ok) throw envelopeError(response.status, payload);
  return (payload as { data: T }).data;
}
